import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const STORAGE_KEY = 'signa_onboarding_complete';

export function isOnboardingComplete(): boolean {
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

function markOnboardingComplete(): void {
  localStorage.setItem(STORAGE_KEY, 'true');
}

interface Step {
  icon: React.ReactNode;
  title: string;
  description: string;
  hint?: string;
}

// SVG icons

function EmailIcon({ size = 48 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="4" y="10" width="40" height="28" rx="3" fill="var(--accent-muted)" stroke="var(--accent)" strokeWidth="2" />
      <path d="M4 13L24 27L44 13" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function TodoIcon({ size = 48 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="6" width="32" height="36" rx="3" fill="var(--bg-elevated)" stroke="var(--accent)" strokeWidth="2" />
      <path d="M16 16H32" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" />
      <path d="M16 24H28" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" />
      <path d="M16 32H24" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" />
      <path d="M32 30L34 32L38 27" stroke="var(--accent)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function NotesIcon({ size = 48 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M10 6H30L38 14V40C38 41.1 37.1 42 36 42H10C8.9 42 8 41.1 8 40V10C8 8.9 8.9 8 10 8V6Z" fill="var(--bg-elevated)" stroke="var(--accent)" strokeWidth="2" />
      <path d="M30 6V14H38" stroke="var(--accent)" strokeWidth="2" strokeLinejoin="round" />
      <path d="M16 22H32" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" />
      <path d="M16 30H28" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" />
      <path d="M16 38H24" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

const STEPS: Step[] = [
  {
    icon: <EmailIcon />,
    title: 'Your emails, organized',
    description:
      'Signa connects to Gmail or Microsoft 365 and brings your inbox front and center. Star important emails, snooze ones you need later, and use the AI bar to get instant summaries.',
    hint: 'Connect an email provider in Settings to get started.',
  },
  {
    icon: <TodoIcon />,
    title: 'Tasks with NLP shortcuts',
    description:
      'Type natural language to create tasks instantly. Try "Call John tomorrow p:1 #Work" - Signa parses the date, priority, and project automatically.',
    hint: 'Press N anywhere in the Todo panel to open the quick-add input.',
  },
  {
    icon: <NotesIcon />,
    title: 'Notes linked to everything',
    description:
      'Daily notes, backlinks, and AI-powered suggestions keep your notes connected to your email and tasks. Press / in any note for formatting shortcuts.',
    hint: 'Link notes to emails or tasks by typing [[ and selecting a result.',
  },
];

interface OnboardingModalProps {
  onComplete: () => void;
}

export function OnboardingModal({ onComplete }: OnboardingModalProps) {
  const [step, setStep] = useState(0);

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      markOnboardingComplete();
      onComplete();
    }
  };

  const handleSkip = () => {
    markOnboardingComplete();
    onComplete();
  };

  const current = STEPS[step]!;
  const isLast = step === STEPS.length - 1;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 flex items-center justify-center"
        style={{ backgroundColor: 'var(--bg-overlay)' }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 8 }}
          transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
          className="relative w-full max-w-sm mx-4 rounded-2xl overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {/* Header */}
          <div
            className="px-8 pt-8 pb-4"
            style={{ backgroundColor: 'var(--bg-elevated)' }}
          >
            <div className="flex items-center justify-between mb-6">
              <span
                className="text-xs font-mono uppercase tracking-wider"
                style={{ color: 'var(--text-tertiary)' }}
              >
                Step {step + 1} of {STEPS.length}
              </span>
              <button
                onClick={handleSkip}
                className="text-xs px-2 py-1 rounded transition-colors"
                style={{ color: 'var(--text-tertiary)', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-tertiary)'; }}
              >
                Skip
              </button>
            </div>

            {/* Step indicator dots */}
            <div className="flex items-center gap-2 mb-6">
              {STEPS.map((_, i) => (
                <div
                  key={i}
                  className="h-1 rounded-full transition-all duration-200"
                  style={{
                    width: i === step ? '32px' : '8px',
                    backgroundColor: i === step ? 'var(--accent)' : 'var(--border-strong)',
                  }}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="px-8 py-6">
            <div className="mb-5">{current.icon}</div>
            <h2
              className="text-xl font-semibold mb-3"
              style={{
                color: 'var(--text-primary)',
                fontFamily: 'DM Sans, system-ui, sans-serif',
              }}
            >
              {current.title}
            </h2>
            <p
              className="text-sm leading-relaxed mb-4"
              style={{ color: 'var(--text-secondary)', fontFamily: 'DM Sans, system-ui, sans-serif' }}
            >
              {current.description}
            </p>
            {current.hint && (
              <p
                className="text-xs rounded-lg px-3 py-2"
                style={{
                  color: 'var(--accent)',
                  backgroundColor: 'var(--accent-muted)',
                  fontFamily: 'JetBrains Mono, monospace',
                }}
              >
                {current.hint}
              </p>
            )}
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-between px-8 py-5"
            style={{ borderTop: '1px solid var(--border)' }}
          >
            <button
              onClick={handleSkip}
              className="px-4 py-2 rounded-lg text-sm transition-colors"
              style={{ color: 'var(--text-secondary)', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-primary)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
            >
              Skip tour
            </button>
            <button
              onClick={handleNext}
              className="px-5 py-2 rounded-lg text-sm font-medium transition-all duration-150"
              style={{
                backgroundColor: 'var(--accent)',
                color: 'var(--text-inverse)',
                cursor: 'pointer',
                border: 'none',
                transform: 'scale(1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--accent-strong)';
                e.currentTarget.style.transform = 'scale(1.02)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--accent)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              {isLast ? 'Get started' : 'Next'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
